'use client'

import { Divider } from '@lastbrand/web-ui/divider'
import { Skeleton } from '@lastbrand/web-ui/skeleton'
import { css } from '@lastbrand/web-ui-styled-system/css'

import InfiniteScrollProductListDS from '@src/components/blocks/infinite-scroll-product-list-ds'
import { TEST_LOCATORS } from '@src/constants/test-locators'
import { SectionContentContainer } from '@src/ds/blocks/section-content-container'
import { trackingMetaData } from '@src/pages/[...slug]/components/collection/collection.constants'
import CollectionFilters from '@src/pages/[...slug]/components/collection/molecules/collectionFilters/CollectionFilters'
import NoProduct from '@src/pages/[...slug]/components/collection/molecules/noProducts'
import { LoaderWrapper } from '@src/quince-components/src'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import { MAX_NUMBER_OF_COLOR_OPTIONS } from '@src/quince-site-common/src/utils/constants'

import { MINI_PDP_COLLECTION_SECTION_NAME_FOR_REF } from '../../organisms/productPageSections/miniPDPCollection/constant'
import { useMiniPDPCollection } from '../../organisms/productPageSections/miniPDPCollection/useMiniPDPCollection'
import { MiniPDPProductImagesDS } from '../../organisms/productPageSections/miniPDPProductImages/mini-pdp-product-images-ds'
import { MiniPDPProductPurchase } from '../../organisms/productPageSections/miniPDPProductPurchase/miniPDPProductPurchase'

import type { ProductPageContext } from '../../domains/MiniPDPContext'

const TRACKING_CATEGORY_NAVIGATION = 'navigation'
const SKELETON_CARD_COUNT = 8

const topRowClass = css({ display: 'flex', flexDirection: { base: 'column', lg: 'row' } })

const productGridClass = css({
  display: 'grid',
  w: 'full',
  gridTemplateColumns: {
    base: 'repeat(2, minmax(0, 1fr))',
    lg: 'repeat(4, minmax(0, 1fr))',
  },
  justifyItems: 'center',
  columnGap: 'space-16',
  rowGap: { base: 'space-32' },
  '& > *': {
    w: 'full',
  },
})

const boundaryClass = css({ height: '1px', visibility: 'hidden' })

function ProductCardSkeleton() {
  return (
    <div>
      <Skeleton css={{ width: 'full', aspectRatio: '4 / 5' }} />
      <Skeleton css={{ width: '70%', height: 'space-16', marginTop: 'space-12' }} />
      <Skeleton css={{ width: '40%', height: 'space-16', marginTop: 'space-8' }} />
    </div>
  )
}

export const MiniPDPDS = ({ pageData }: { pageData: ProductPageContext }) => {
  const {
    collectionPage,
    totalProductsCount,
    initialFilters,
    sortBy,
    selectedFilters,
    updateQuery,
    showSubCollectionsPageLoader,
    showNextFeedLoading,
    filteredSubCollection,
    totalProductsCurrentlyOnPage,
    intersectRef,
    productViewTracker,
    filterSectionRef,
    productSectionRef,
    productCardsSectionRef,
    tracker,
  } = useMiniPDPCollection({
    collectionId: pageData.product.productRecommendations.mpdp.data.id,
    product: pageData.product,
    sectionData: pageData.sectionData,
  })

  const { deviceType } = useDeviceInfo()
  const isDesktop = deviceType === DEVICE_TYPES.DESKTOP
  const eagerLoadCount = isDesktop ? 4 : 2

  return (
    <>
      <SectionContentContainer contentClassName={topRowClass}>
        <MiniPDPProductImagesDS sectionData={pageData.sectionData} product={pageData.product} />
        <MiniPDPProductPurchase product={pageData.product} sectionData={pageData.sectionData} />
      </SectionContentContainer>

      <Divider css={{ marginTop: { base: 'space-24', lg: 'space-32' } }} />

      <div id="filter-section" ref={filterSectionRef}>
        <CollectionFilters
          totalProductsCount={totalProductsCount}
          initialFilters={initialFilters}
          sortBy={sortBy}
          selectedFilters={selectedFilters}
          updateQuery={updateQuery}
          collectionPage={collectionPage}
          showLoader={showSubCollectionsPageLoader && initialFilters.length === 0}
          subCollectionCards={[]}
        />
      </div>

      <div ref={productSectionRef} data-section-name={MINI_PDP_COLLECTION_SECTION_NAME_FOR_REF}>
        <SectionContentContainer>
          {showSubCollectionsPageLoader ? (
            <div className={productGridClass}>
              {Array.from({ length: isDesktop ? SKELETON_CARD_COUNT : SKELETON_CARD_COUNT / 2 }).map((_, index) => (
                <ProductCardSkeleton key={index} />
              ))}
            </div>
          ) : (
            <>
              <InfiniteScrollProductListDS
                productCards={filteredSubCollection?.productCards ?? []}
                swiperClassName={productGridClass}
                optionLimit={MAX_NUMBER_OF_COLOR_OPTIONS}
                tracker={tracker}
                productViewTracker={productViewTracker}
                productClickTrackConfig={{
                  trackInternal: true,
                  trackGA: true,
                  category: TRACKING_CATEGORY_NAVIGATION,
                  action: 'click_subcollection_product',
                  context: { ...trackingMetaData.context },
                }}
                showWhiteGloveDeliveryTag
                eagerLoadImageCount={eagerLoadCount}
              />
              <div id="boundary_bottom" className={boundaryClass} ref={intersectRef} />
              <LoaderWrapper
                testid={TEST_LOCATORS.COLLECTION_INFINITE_LOADER}
                showLoader={showNextFeedLoading}
                minHeight={'40rem'}
              >
                <div />
              </LoaderWrapper>
            </>
          )}
        </SectionContentContainer>
      </div>
      {totalProductsCurrentlyOnPage === 0 && !showSubCollectionsPageLoader && (
        <NoProduct
          onResetFilters={() => {
            updateQuery({
              filter: { type: 'reset' },
              sort: { type: 'reset' },
            })
          }}
          ref={productCardsSectionRef}
        />
      )}
    </>
  )
}
